// src/pages/BOMMasterForm.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Form,
  Row,
  Col,
  Button,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionBody,
} from "react-bootstrap";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import axios from "axios";
import { toast } from "react-toastify";
import "../Styling/Title.css";
import "../Styling/Accordion.css";
import "../Styling/SaveButton.css";
import "../Styling/ResetButton.css";
import "../Styling/BomMaster.css";

const initialForm = {
  organisationId: "",
  userId: "",
  officeCode: "",
  bomType: "",
  parentItemCodeId: "",
  componentItemCodeId: "",
  quantityPerParent: "",
  unitCode: "",
  scrapPercentage: "",
  effectiveFromDate: "",
  effectiveToDate: "",
};

export default function BOMMasterForm() {
  const { user } = useAuth();
  const [form, setForm] = useState(initialForm);

  // lookups
  const [offices, setOffices] = useState([]);
  const [bomTypes, setBomTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);

  const [loading, setLoading] = useState({
    offices: false,
    bomTypes: false,
    items: false,
    units: false,
  });
  const [errors, setErrors] = useState({});

  // Prefill read-only fields
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      organisationId: user?.organisationId ?? "",
      userId: user?.userId ?? "",
    }));
  }, [user]);

  // Helpers
  const safeFetchJson = useCallback(async (url) => {
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} ${res.statusText} – ${text}`);
    }
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const text = await res.text();
      return text ? JSON.parse(text) : [];
    }
    return res.json();
  }, []);

  const loadOffices = useCallback(
    async (orgId) => {
      if (!orgId) return setOffices([]);
      setLoading((s) => ({ ...s, offices: true }));
      try {
        const data = await safeFetchJson(
          `${Base_URL}/Office/OfficeByOrgId/${orgId}`
        );
        setOffices(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setOffices([]);
      } finally {
        setLoading((s) => ({ ...s, offices: false }));
      }
    },
    [safeFetchJson]
  );

  const loadAllLookups = useCallback(async () => {
    setLoading((s) => ({ ...s, bomTypes: true, items: true, units: true }));
    try {
      const [bomTypeData, itemData, unitData] = await Promise.all([
        safeFetchJson(`${Base_URL}/BOM/bomtypes`), // show bomType1 - bomTypeDescription
        safeFetchJson(`${Base_URL}/Item/ItemGetData`), // itemCodeId - itemName
        safeFetchJson(`${Base_URL}/UnitMaster/GetAllUnits`), // code - description
      ]);
      setBomTypes(Array.isArray(bomTypeData) ? bomTypeData : []);
      setItems(Array.isArray(itemData) ? itemData : []);
      setUnits(Array.isArray(unitData) ? unitData : []);
    } catch (e) {
      console.error(e);
      setBomTypes([]);
      setItems([]);
      setUnits([]);
    } finally {
      setLoading((s) => ({
        ...s,
        bomTypes: false,
        items: false,
        units: false,
      }));
    }
  }, [safeFetchJson]);

  // Load lookups on mount and reload offices when org changes
  useEffect(() => {
    loadAllLookups();
  }, [loadAllLookups]);

  useEffect(() => {
    loadOffices(user?.organisationId ?? "");
  }, [user?.organisationId, loadOffices]);

  const anyLoading = useMemo(
    () => loading.offices || loading.bomTypes || loading.items || loading.units,
    [loading]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // Validation
  const validate = () => {
    const next = {};
    if (!form.organisationId) next.organisationId = "Required";
    if (!form.userId) next.userId = "Required";
    if (!form.officeCode) next.officeCode = "Select Office";
    if (!form.bomType) next.bomType = "Select BOM Type";
    if (!form.parentItemCodeId) next.parentItemCodeId = "Select Parent Item";
    if (!form.componentItemCodeId)
      next.componentItemCodeId = "Select Component Item";
    if (
      form.quantityPerParent === "" ||
      Number.isNaN(Number(form.quantityPerParent)) ||
      Number(form.quantityPerParent) <= 0
    ) {
      next.quantityPerParent = "Enter a valid quantity (> 0)";
    }
    if (!form.unitCode) next.unitCode = "Select Unit";
    if (
      form.scrapPercentage === "" ||
      Number.isNaN(Number(form.scrapPercentage)) ||
      Number(form.scrapPercentage) < 0 ||
      Number(form.scrapPercentage) > 100
    ) {
      next.scrapPercentage = "0 to 100 allowed";
    }
    if (!form.effectiveFromDate) next.effectiveFromDate = "Select From date";
    if (!form.effectiveToDate) next.effectiveToDate = "Select To date";
    if (form.effectiveFromDate && form.effectiveToDate) {
      if (new Date(form.effectiveToDate) < new Date(form.effectiveFromDate)) {
        next.effectiveToDate = "To date must be after From date";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // number cleaner
  const toNumberOrNull = (val) => {
    if (val === "" || val === null || val === undefined) return null;
    const num = Number(val);
    return Number.isNaN(num) ? null : num;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      organisationId:
        toNumberOrNull(form.organisationId) ?? form.organisationId, // keep number if numeric
      userId: toNumberOrNull(form.userId) ?? form.userId,
      officeCode: form.officeCode, // string code from Office
      bomType: form.bomType, // bomType1
      parentItemCodeId: form.parentItemCodeId, // itemCodeId
      componentItemCodeId: form.componentItemCodeId, // itemCodeId
      quantityPerParent: toNumberOrNull(form.quantityPerParent),
      unitCode: form.unitCode, // code from Unit
      scrapPercentage: toNumberOrNull(form.scrapPercentage),
      effectiveFromDate: form.effectiveFromDate, // YYYY-MM-DD
      effectiveToDate: form.effectiveToDate, // YYYY-MM-DD
    };

    try {
      const res = await axios.post(`${Base_URL}/BOM/createBOM`, payload);
      if (res.status === 200)
        toast.success(res.data, "BOM CREATED SUCCESSFULLY");
    } catch (Error) {
      toast.error("Error Creating BOM");
    }
  };

  const labelOr = (x) => (x ?? "").toString().trim() || "-";

  return (
    <div className="container bom-master-fields">
      <h2 className="fields-title">BOM Master</h2>
      {anyLoading && (
        <span className="d-inline-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" /> Loading…
        </span>
      )}

      <Form onSubmit={handleSubmit} noValidate className="fields-form">
        <Accordion defaultActiveKey="0" className="bom-master-accordion">
          <AccordionItem eventKey="0">
            <AccordionBody>
              {/* NOTE: Moved the Form inside AccordionBody but Row/Col outside AccordionBody was the issue */}
              <Row className="g-3">
                {" "}
                {/* Added g-3 for proper gutter spacing */}
                {/* Organisation Id (read-only) */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <Form.Group controlId="organisationId">
                    <Form.Label>Organisation Id</Form.Label>
                    <Form.Control
                      type="text"
                      name="organisationId"
                      value={form.organisationId}
                      readOnly
                      isInvalid={!!errors.organisationId}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.organisationId}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {/* User Id (read-only) */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <Form.Group controlId="userId">
                    <Form.Label>User Id</Form.Label>
                    <Form.Control
                      type="text"
                      name="userId"
                      value={form.userId}
                      readOnly
                      isInvalid={!!errors.userId}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.userId}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {/* Office Code */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <Form.Group controlId="officeCode">
                    <Form.Label>Office Code</Form.Label>
                    <Form.Select
                      name="officeCode"
                      value={form.officeCode}
                      onChange={handleChange}
                      disabled={loading.offices}
                      isInvalid={!!errors.officeCode}
                    >
                      <option value="">-- Select Office --</option>
                      {offices.map((o, idx) => (
                        <option key={o.officeCode ?? idx} value={o.officeCode}>
                          {`${labelOr(o.officeCode)} - ${labelOr(
                            o.officeName || o.office_Description
                          )}`}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.officeCode}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {/* BOM Type */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <Form.Group controlId="bomType">
                    <Form.Label>BOM Type</Form.Label>
                    <Form.Select
                      name="bomType"
                      value={form.bomType}
                      onChange={handleChange}
                      disabled={loading.bomTypes}
                      isInvalid={!!errors.bomType}
                    >
                      <option value="">-- Select BOM Type --</option>
                      {bomTypes.map((b, idx) => (
                        <option
                          key={b.bomType1 ?? idx}
                          value={b.bomType1 ?? b.bomType}
                        >
                          {`${labelOr(b.bomType1 ?? b.bomType)} - ${labelOr(
                            b.bomTypeDescription
                          )}`}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.bomType}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {/* Parent Item Code Id */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <Form.Group controlId="parentItemCodeId">
                    <Form.Label>Parent Item Code</Form.Label>
                    <Form.Select
                      name="parentItemCodeId"
                      value={form.parentItemCodeId}
                      onChange={handleChange}
                      disabled={loading.items}
                      isInvalid={!!errors.parentItemCodeId}
                    >
                      <option value="">-- Select Parent Item --</option>
                      {items.map((it) => (
                        <option key={it.itemCodeId} value={it.itemCodeId}>
                          {`${labelOr(it.itemCodeId)} - ${labelOr(
                            it.itemName
                          )}`}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.parentItemCodeId}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {/* Component Item Code Id */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <Form.Group controlId="componentItemCodeId">
                    <Form.Label>Component Item Code</Form.Label>
                    <Form.Select
                      name="componentItemCodeId"
                      value={form.componentItemCodeId}
                      onChange={handleChange}
                      disabled={loading.items}
                      isInvalid={!!errors.componentItemCodeId}
                    >
                      <option value="">-- Select Component Item --</option>
                      {items.map((it) => (
                        <option key={it.itemCodeId} value={it.itemCodeId}>
                          {`${labelOr(it.itemCodeId)} - ${labelOr(
                            it.itemName
                          )}`}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.componentItemCodeId}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {/* Quantity Per Parent */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <Form.Group controlId="quantityPerParent">
                    <Form.Label>Quantity Per Parent</Form.Label>
                    <Form.Control
                      type="number"
                      name="quantityPerParent"
                      inputMode="decimal"
                      step="0.0001"
                      min="0"
                      value={form.quantityPerParent}
                      onChange={handleChange}
                      isInvalid={!!errors.quantityPerParent}
                      placeholder="e.g., 1.5"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.quantityPerParent}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {/* Unit Code */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <Form.Group controlId="unitCode">
                    <Form.Label>Unit Code</Form.Label>
                    <Form.Select
                      name="unitCode"
                      value={form.unitCode}
                      onChange={handleChange}
                      disabled={loading.units}
                      isInvalid={!!errors.unitCode}
                    >
                      <option value="">-- Select Unit --</option>
                      {units.map((u) => (
                        <option key={u.code} value={u.code}>
                          {`${labelOr(u.code)} - ${labelOr(u.description)}`}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.unitCode}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {/* Scrap Percentage */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <Form.Group controlId="scrapPercentage">
                    <Form.Label>Scrap Percentage (%)</Form.Label>
                    <Form.Control
                      type="number"
                      name="scrapPercentage"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      max="100"
                      value={form.scrapPercentage}
                      onChange={handleChange}
                      isInvalid={!!errors.scrapPercentage}
                      placeholder="0 - 100"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.scrapPercentage}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {/* Effective From/To Dates */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <Form.Group controlId="effectiveFromDate">
                    <Form.Label>Effective From Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="effectiveFromDate"
                      value={form.effectiveFromDate}
                      onChange={handleChange}
                      isInvalid={!!errors.effectiveFromDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.effectiveFromDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xl={4} lg={4} md={6} sm={12}>
                  <Form.Group controlId="effectiveToDate">
                    <Form.Label>Effective To Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="effectiveToDate"
                      value={form.effectiveToDate}
                      onChange={handleChange}
                      isInvalid={!!errors.effectiveToDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.effectiveToDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </AccordionBody>
          </AccordionItem>
        </Accordion>

        {/* Actions */}
        <div className="fields-btn-container" style={{ marginTop: 16 }}>
          <Button
            type="button"
            variant="secondary"
            className="fields-reset"
            onClick={() => {
              setForm({
                ...initialForm,
                organisationId: user?.organisationId ?? "",
                userId: user?.userId ?? "",
              });
              setErrors({});
            }}
          >
            Reset
          </Button>

          <Button
            type="submit"
            variant="primary"
            className="fields-submit"
            disabled={anyLoading}
          >
            {anyLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Please wait…
              </>
            ) : (
              "Save "
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
}
